import {LeafletVectorSource} from '../classes/leaflet-vector-source';
import {LeafletFeature} from '../classes/leaflet-feature';
import {MapService} from '../../map/services/map.service';
import L from 'leaflet';
import {LeafletMapLayer} from '../classes/leaflet-map-layer';
import {NormalOutageData} from '../classes/normal-outage-data';
import {min as d3Min, max as d3Max, scaleLinear, scaleQuantile} from 'd3';
import {inject} from '@angular/core';
import {NormalDataService} from 'frontend/src/app/services/normal.service';
import {PopoverElement} from 'frontend/src/app/classes/popover-element';

export class NormalOutageDataSource extends LeafletVectorSource<NormalOutageData> {
    private normalDataService = inject(NormalDataService);

    constructor(layerInfo: LeafletMapLayer) {
        super(layerInfo);
        this.showAllPopoverData = false;
    }

    processFeatures(features?: NormalOutageData[], valueColumn: 'normalValue' | 'countAboveNormal' = 'normalValue'): void {
        // Build out the color scale used to color each geometry
        const values = features.map((f) => f[valueColumn]);
        // Get the min and max values for the set
        const min = d3Min(values);
        const max = d3Max(values);
        // Create a linear scale from the min and the max and convert it to a 0-100 scale
        const linearScale = scaleLinear([min, max], [0, 100]);
        // Using that scale, transpose each value to its scaled value and then assign it to a particular color in the
        // colorScale
        const quantileScale = scaleQuantile([...new Set(values.map((v) => linearScale(v)))], this.normalDataService.colorScale());

        this.normalDataService.setScaleTools(quantileScale, linearScale);

        this.leafletFeatures = features
            ?.map((v) => {
                const feature = new LeafletFeature().convert(v);
                if (feature) {
                    feature.buildPopover(this.layerInfo);
                }
                return feature;
            })
            .filter((feature) => !!feature);

        const config: any = {
            pane: MapService.layerPaneName,
            onEachFeature: (feature: LeafletFeature<NormalOutageData>, layer: L.GeoJSON) => {
                // Build the popover data
                const props = feature.properties;
                const popoverData: PopoverElement[] = [
                    new PopoverElement('', 'Normal Outage Data').setTitle(),
                    new PopoverElement('State Name', props.stateName),
                    new PopoverElement('County Name', props.countyName),
                    new PopoverElement('Utility', props.utilityName),
                    new PopoverElement('Normal Value', props.normalValue.toString()),
                    new PopoverElement('Count Above Normal', props.countAboveNormal.toString()),
                ];

                this.initializePopoverInteractions(feature, layer, 'click', false, popoverData);
            },
            style: (feature: LeafletFeature<NormalOutageData>) => {
                const props = feature.properties;
                // To get the correct color we need to scale the chose value and then pass it into the
                // quantile scale to match it to the color
                const color = quantileScale(linearScale(props[valueColumn]));

                return {
                    color,
                    weight: 2,
                    stroke: true,
                    fillOpacity: 0.5,
                };
            },
        };

        this.source = L.geoJSON(this.leafletFeatures as any, config);
    }
}
